/**
 * Prompt Storage Service
 *
 * Handles persistence of saved prompts in localStorage.
 * Reads/writes from the same storage location as GlobalStateManager
 * (`carousel_settings`) to keep prompts in sync between dialog and taskpane.
 */
const SETTINGS_KEY = "carousel_settings";
function isPromptHost(value) {
    return value === "powerpoint" || value === "excel";
}
function getPromptHostFromQueryString() {
    try {
        if (typeof window === "undefined") {
            return null;
        }
        const host = new URLSearchParams(window.location.search).get("host");
        return isPromptHost(host) ? host : null;
    }
    catch {
        return null;
    }
}
export function getCurrentPromptHost() {
    const fromQuery = getPromptHostFromQueryString();
    if (fromQuery) {
        return fromQuery;
    }
    try {
        const office = globalThis.Office;
        const host = String(office?.context?.host || "").toLowerCase();
        if (host.includes("excel")) {
            return "excel";
        }
    }
    catch {
        // Fall through to PowerPoint default.
    }
    return "powerpoint";
}
function normalizePrompt(raw, fallbackHost) {
    if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
        return null;
    }
    const value = raw;
    const id = typeof value.id === "string" ? value.id.trim() : "";
    const title = typeof value.title === "string" ? value.title : "";
    const content = typeof value.content === "string" ? value.content : "";
    const host = isPromptHost(value.host) ? value.host : fallbackHost ?? null;
    if (!id || !title.trim() || !content.trim() || !host) {
        return null;
    }
    return {
        id,
        title,
        content,
        host,
    };
}
function readSettings() {
    try {
        const raw = localStorage.getItem(SETTINGS_KEY);
        if (!raw) {
            return {};
        }
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
            return {};
        }
        return parsed;
    }
    catch {
        return {};
    }
}
function readAllPrompts(settings) {
    const source = settings ?? readSettings();
    const rawPrompts = source.promptLibrary;
    if (!Array.isArray(rawPrompts)) {
        return [];
    }
    const prompts = [];
    for (const item of rawPrompts) {
        // Hard-cut behavior: prompts without a valid host are ignored.
        const normalized = normalizePrompt(item);
        if (normalized) {
            prompts.push(normalized);
        }
    }
    return prompts;
}
/**
 * Load host-scoped prompts from localStorage.
 */
export function loadPrompts(host = getCurrentPromptHost()) {
    try {
        return readAllPrompts().filter((prompt) => prompt.host === host);
    }
    catch (error) {
        console.error("Error loading prompts:", error);
        return [];
    }
}
/**
 * Save host-scoped prompts to localStorage while preserving the other host.
 */
export function savePrompts(prompts, host = getCurrentPromptHost()) {
    try {
        const settings = readSettings();
        const existingPrompts = readAllPrompts(settings);
        const preservedOtherHostPrompts = existingPrompts.filter((prompt) => prompt.host !== host);
        const normalizedHostPrompts = [];
        for (const prompt of prompts) {
            const normalized = normalizePrompt(prompt, host);
            if (normalized && normalized.host === host) {
                normalizedHostPrompts.push(normalized);
            }
        }
        settings.promptLibrary = [...preservedOtherHostPrompts, ...normalizedHostPrompts];
        localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
    }
    catch (error) {
        console.error("Error saving prompts:", error);
        throw error;
    }
}
/**
 * Clear prompts only for the active host.
 */
export function clearPrompts(host = getCurrentPromptHost()) {
    try {
        const settings = readSettings();
        const retainedPrompts = readAllPrompts(settings).filter((prompt) => prompt.host !== host);
        settings.promptLibrary = retainedPrompts;
        localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
    }
    catch (error) {
        console.error("Error clearing prompts:", error);
        throw error;
    }
}
